import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { useAuthCtx } from '../App';
import api from '../utils/api';

const CATEGORIES = [
  'Order issue',
  'Shipping & delivery',
  'Product / COA question',
  'Billing & payment',
  'Account',
  'Other',
];

const SUPPORT_EMAIL = 'support@alohaamino.com';

/** Success panel shown after a ticket / feature request is filed. */
function SuccessCard({ ticket, onReset }) {
  const isFeature = ticket.type === 'feature';
  return (
    <div className="card text-center py-10">
      <div className="w-16 h-16 rounded-full bg-tea-100 text-tea-600 flex items-center justify-center mx-auto mb-5">
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h3 className="text-xl font-extrabold text-gray-900 dark:text-slate-100 mb-2">
        {isFeature ? 'Feature request submitted!' : "Thanks — we've got your message"}
      </h3>
      <p className="text-gray-500 dark:text-slate-400 max-w-md mx-auto mb-5">
        {isFeature
          ? 'Our team reviews every idea. We sent a confirmation to your email.'
          : 'A member of our lab-support team will reply by email, usually within one business day.'}
      </p>
      <div className="inline-flex flex-col items-center bg-tea-50 dark:bg-tea-900/20 border border-tea-200 dark:border-tea-800 rounded-2xl px-8 py-4 mb-6">
        <span className="text-xs uppercase tracking-widest text-tea-700 font-semibold mb-1">Your reference</span>
        <span className="font-mono text-2xl font-bold text-gray-900 dark:text-slate-100 tracking-wider">{ticket.reference}</span>
      </div>
      <div>
        <button onClick={onReset} className="btn-secondary text-sm px-6 py-2.5">Submit another</button>
      </div>
    </div>
  );
}

export default function ContactPage() {
  const { user } = useAuthCtx() || {};
  const [tab, setTab] = useState('support'); // 'support' | 'feature'
  const [submitted, setSubmitted] = useState(null);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    name: user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '',
    email: user?.email || '',
    category: CATEGORIES[0],
    orderRef: '',
    subject: '',
    message: '',
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const switchTab = (t) => {
    setTab(t);
    setSubmitted(null);
    setForm((f) => ({ ...f, subject: '', message: '', category: CATEGORIES[0], orderRef: '' }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        type: tab,
        name: form.name.trim() || undefined,
        email: form.email.trim(),
        subject: form.subject.trim(),
        message: form.message.trim(),
        pageUrl: window.location.href,
        ...(tab === 'support' ? { category: form.category, orderRef: form.orderRef.trim() || undefined } : {}),
      };
      const { data } = await api.post('/tickets', payload);
      setSubmitted(data.ticket);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col dark:bg-slate-900">
      <Navbar />

      {/* Hero */}
      <section className="bg-tea-700 text-white py-14 md:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-tea-200 text-sm font-semibold uppercase tracking-widest mb-3">Get in touch</p>
          <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight leading-tight">Contact us</h1>
          <p className="mt-4 text-tea-100 text-lg max-w-xl">
            Open a support ticket, ask a question, or tell us what to build next. We reply Mon–Fri, 9 AM–5 PM ET.
          </p>
        </div>
      </section>

      {/* Quick contact strip */}
      <div className="bg-tea-50 border-b border-tea-200 dark:bg-slate-800 dark:border-slate-700">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-wrap items-center gap-x-8 gap-y-2 text-sm">
          <a href={`mailto:${SUPPORT_EMAIL}`} className="flex items-center gap-2 text-tea-800 dark:text-tea-300 font-medium hover:underline">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
            </svg>
            {SUPPORT_EMAIL}
          </a>
          <Link to="/track" className="flex items-center gap-2 text-tea-800 dark:text-tea-300 font-medium hover:underline">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 17a2 2 0 11-4 0 2 2 0 014 0zm10 0a2 2 0 11-4 0 2 2 0 014 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 16V6a1 1 0 00-1-1H3m10 11h2m4 0h1.5a.5.5 0 00.5-.5V12l-3-4h-4" />
            </svg>
            Track an order
          </Link>
          <Link to="/support" className="flex items-center gap-2 text-tea-800 dark:text-tea-300 font-medium hover:underline">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Browse the FAQ
          </Link>
        </div>
      </div>

      <main id="main-content" tabIndex={-1} className="flex-1 py-12 md:py-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Tabs */}
          <div className="flex bg-gray-100 dark:bg-slate-800 rounded-pill p-1 mb-8">
            {[
              { id: 'support', label: 'Support ticket' },
              { id: 'feature', label: '💡 Request a feature' },
            ].map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => switchTab(t.id)}
                className={
                  'flex-1 py-2.5 text-sm font-semibold rounded-pill transition-colors ' +
                  (tab === t.id ? 'bg-white text-tea-700 shadow-sm dark:bg-slate-700 dark:text-tea-300' : 'text-gray-500 dark:text-slate-400')
                }
              >
                {t.label}
              </button>
            ))}
          </div>

          {submitted ? (
            <SuccessCard ticket={submitted} onReset={() => switchTab(tab)} />
          ) : (
            <div className="card">
              {tab === 'feature' && (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 mb-6 text-sm text-amber-800 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-300">
                  Got an idea for a product, feature, or improvement? We read every request and prioritise the most-asked-for ones.
                </div>
              )}

              <form onSubmit={submit} className="flex flex-col gap-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-1.5">Name</label>
                    <input className="input-field" placeholder="Your name" value={form.name} onChange={set('name')} maxLength={120} />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-1.5">Email <span className="text-red-500">*</span></label>
                    <input type="email" required className="input-field" placeholder="you@lab.edu" value={form.email} onChange={set('email')} maxLength={160} />
                  </div>
                </div>

                {tab === 'support' && (
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-1.5">Topic</label>
                      <select className="input-field" value={form.category} onChange={set('category')}>
                        {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-1.5">
                        Order number <span className="text-gray-400 font-normal">(optional)</span>
                      </label>
                      <input className="input-field font-mono uppercase" placeholder="e.g. 3F9A1C2B" value={form.orderRef} onChange={set('orderRef')} maxLength={60} />
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-1.5">
                    {tab === 'feature' ? 'What would you like to see?' : 'Subject'} <span className="text-red-500">*</span>
                  </label>
                  <input
                    required
                    className="input-field"
                    placeholder={tab === 'feature' ? 'e.g. Add saved shipping addresses' : 'Brief summary of your question'}
                    value={form.subject}
                    onChange={set('subject')}
                    minLength={3}
                    maxLength={200}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-1.5">
                    {tab === 'feature' ? 'Tell us more' : 'Message'} <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    required
                    rows={6}
                    className="input-field resize-y"
                    placeholder={tab === 'feature'
                      ? 'What problem would this solve for you? How would you use it?'
                      : 'Share as much detail as you can so we can help quickly.'}
                    value={form.message}
                    onChange={set('message')}
                    minLength={10}
                    maxLength={5000}
                  />
                  <p className="text-xs text-gray-400 mt-1">{form.message.length}/5000</p>
                </div>

                <button type="submit" className="btn-primary w-full" disabled={loading}>
                  {loading ? 'Sending…' : tab === 'feature' ? 'Submit feature request' : 'Submit ticket'}
                </button>
                <p className="text-xs text-center text-gray-400">
                  You'll receive a reference number by email to track your {tab === 'feature' ? 'request' : 'ticket'}.
                </p>
              </form>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
